// ── program-cohorts-section: the assigned-cohorts READ on program detail ────
// Wave 2 (Programs). CONTRACT CHECK (source over PRD): the backend exposes ONLY
// programs.list / create / detail / update (coaching-contracts.ts lines 123-130)
// — there is NO program->cohort assignment write endpoint. A cohort carries a
// SINGLE programId (CoachingCohortDto.program_id / program_name), so a program's
// "assignment" is the set of cohorts whose program_id === this program. That
// linkage is SET at cohort create/edit (Wave 3 — Cohorts), NOT here. This panel
// therefore surfaces the assigned cohorts as a READ-ONLY list and states, in the
// UI and in this comment, that assignment is managed cohort-side. We do NOT
// invent an assign-to-cohorts write flow.
//
// The panel owns its OWN useHonestQuery -> <HonestState> read (scoped to this
// program via the list's company/program filter), so an unavailable /
// permission-denied / empty cohorts read never blanks the program detail and
// never reads as a fabricated empty. The list is a server-capped bare array (no
// pagination); the FE filter on program_id is belt-and-suspenders.

import { Alert, Badge, Module, ModuleBody, ModuleHead, humanizeLabel } from "@vjal/ui";
import { Link } from "react-router-dom";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import type { CoachingCohortDto, ListResponse } from "../../services/coaching-dtos";

const services = getCoachingServices();

function cohortRowsFor(programId: string, cohorts: CoachingCohortDto[]): CoachingCohortDto[] {
  return cohorts.filter((cohort) => cohort.program_id === programId);
}

export function ProgramCohortsSection({ programId }: { programId: string }) {
  const query = useHonestQuery<ListResponse<CoachingCohortDto>>(
    coachingQueryKeys.list("cohorts.list", { program_id: programId }),
    () => services.cohorts.list({ program_id: programId }),
    { isEmpty: (data) => cohortRowsFor(programId, data.items).length === 0 }
  );
  const rows = cohortRowsFor(programId, query.data?.items ?? []);

  return (
    <Module span={12}>
      <ModuleHead
        title="Assigned cohorts"
        subtitle="Cohorts running under this program."
       />
      <ModuleBody className="flex flex-col gap-4">
        <Alert variant="info">
          Cohort assignment is managed when a cohort is created or edited (each cohort selects one program). This list is
          read-only here.
        </Alert>

        {query.posture !== null ? (
          <HonestState
            posture={query.posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={query.posture === "empty" ? "No cohorts assigned yet" : undefined}
            message={
              query.posture === "empty"
                ? "Assign this program to a cohort from the cohort's create or edit form."
                : undefined
            }
           />
        ) : (
          <ul className="m-0 flex flex-col gap-2 p-0" aria-label="Assigned cohorts">
            {rows.map((cohort) => (
              <li
                key={cohort.id}
                className="flex items-center justify-between rounded-lg border border-line bg-white px-3 py-2"
              >
                <Link
                  to={`/cohorts/${encodeURIComponent(cohort.id)}`}
                  className="text-sm font-medium text-link no-underline hover:underline"
                  data-exact-set-name="Open cohort">
                  {cohort.name || "Unnamed cohort"}
                </Link>
                <Badge variant={cohort.status === "active" ? "success" : "warning"}>
                  {humanizeLabel(cohort.status ?? "active")}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </ModuleBody>
    </Module>
  );
}
